import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import FacebookIcon from "./facebook-icon";
import CheckmarkIcon from "../icon-checkmark/icon-checkmark";
import LinkIcon from "./link-icon";
import { useText } from "../../core/utils/text";
import useCopyToClipboard from "../../core/utils/useCopyToClipboard";

type ButtonShareProps = {
  className?: string;
};

const ButtonShare: React.FC<ButtonShareProps> = ({ className }) => {
  const t = useText();
  const { href } = window.location;
  const facebookShareUrl = "https://www.facebook.com/sharer/sharer.php";
  const shareUrl = `${facebookShareUrl}?u=${encodeURIComponent(href)}`;

  const shareButtonAriaLabel = t("shareOnFacebookAriaLabelText");
  const copyButtonAriaLabel = t("copyLinkAriaLabelText");

  const [showFixedButtons, setShowFixedButtons] = useState(true);
  const { isCopied, copyToClipboard } = useCopyToClipboard();
  const shareButtonRef = useRef<HTMLAnchorElement>(null);

  const onShareButtonClick = () => {
    copyToClipboard(href);
  };

  // Hide fixed buttons when share button is in view or above viewport
  useEffect(() => {
    const shareButton = shareButtonRef.current;
    let observer: IntersectionObserver | undefined;

    if (shareButton) {
      observer = new IntersectionObserver(
        (entries) => {
          const isIntersecting = entries.some((entry) => entry.isIntersecting);
          const isAboveViewport = entries.some((entry) => {
            // return true if the share button is above the viewport
            return entry.boundingClientRect.top < 0;
          });
          setShowFixedButtons(!isIntersecting && !isAboveViewport);
        },
        { threshold: 1 }
      );
      observer.observe(shareButton);
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  const copyIcon = isCopied ? <CheckmarkIcon /> : <LinkIcon />;

  return (
    <div className={clsx("button-share", className)}>
      {showFixedButtons && (
        <div className="button-share button-share--fixed">
          <a
            href={shareUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={shareButtonAriaLabel}
            className="button-share__button button-share__button--fixed"
          >
            <FacebookIcon />
          </a>
          <button
            type="button"
            onClick={onShareButtonClick}
            aria-label={copyButtonAriaLabel}
            className="button-share__button button-share__button--fixed"
          >
            {copyIcon}
          </button>
        </div>
      )}
      <a
        ref={shareButtonRef}
        href={shareUrl}
        target="_blank"
        rel="noreferrer"
        aria-label={shareButtonAriaLabel}
        className="button-share__button"
      >
        <FacebookIcon />
        {t("shareOnFacebookText")}
      </a>
      <button
        type="button"
        onClick={onShareButtonClick}
        aria-label={copyButtonAriaLabel}
        className="button-share__button"
      >
        {copyIcon}
        <span aria-live="polite">
          {isCopied ? t("copyLinkSuccessText") : t("copyLinkText")}
        </span>
      </button>
    </div>
  );
};

export default ButtonShare;
